/**
 * Google Identity Services, narrowed to what we use.
 *
 * The button is theirs, not ours: hand-building it is against Google's
 * branding terms and loses One Tap. That makes the remote script a real
 * dependency, so everything here degrades to "not configured" rather than
 * throwing when it has not loaded.
 */

export interface GoogleCredentialResponse {
  credential: string;
}

export interface GoogleIdentity {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
      }): void;
      renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
    };
  };
}

export const GSI_SRC = 'https://accounts.google.com/gsi/client';

export function googleIdentity(): GoogleIdentity | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as { google?: GoogleIdentity }).google ?? null;
}

/** Resolves once the script is present, or immediately if it already is. */
export async function loadGoogleScript(): Promise<GoogleIdentity | null> {
  if (typeof document === 'undefined') return null;
  const existing = googleIdentity();
  if (existing !== null) return existing;

  await new Promise<void>((resolve) => {
    const already = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (already !== null) {
      already.addEventListener('load', () => { resolve(); }, { once: true });
      already.addEventListener('error', () => { resolve(); }, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    // Resolving on error too: the screen says "not configured" rather than
    // hanging on a spinner nobody can clear.
    script.addEventListener('load', () => { resolve(); }, { once: true });
    script.addEventListener('error', () => { resolve(); }, { once: true });
    document.head.appendChild(script);
  });

  return googleIdentity();
}
